import { useEffect, useRef, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';
import { sanitizeAdditionalInput } from '../utils/additionalInputsHandler';

const COMMIT_DELAY_MS = 400;

// A discount / loading input that keeps what is being typed to itself and only hands the
// value to `onCommit` once typing pauses (or the field loses focus). The values live on the
// quotation page, so committing every keystroke would re-render every insurer table.
export default function PercentInput({ value, onCommit, ...props }) {
  const [draft, setDraft] = useState(value ?? '');
  const timeoutRef = useRef(null);
  const draftRef = useRef(draft);
  const valueRef = useRef(value);
  const onCommitRef = useRef(onCommit);

  valueRef.current = value;
  onCommitRef.current = onCommit;

  // Follow the value when it is changed from outside, e.g. viewing a saved option.
  useEffect(() => {
    const next = value ?? '';

    draftRef.current = next;
    setDraft(next);
  }, [value]);

  function commit() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (String(draftRef.current) !== String(valueRef.current ?? '')) {
      onCommitRef.current(draftRef.current);
    }
  }

  // Don't lose a pending value when the input goes away.
  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      onCommitRef.current(draftRef.current);
    }
  }, []);

  function handleChange(e) {
    let raw = e.target.value;

    // Typing over the 0 placeholder replaces it, wherever the cursor was (so "0" + "5"
    // is 5, never 50). A decimal point keeps it, for "0.5".
    if (String(draftRef.current) === '0' && !raw.includes('.')) {
      raw = raw.replace('0', '');
    }

    // Clearing the field leaves 0 instead of an empty box.
    const next = sanitizeAdditionalInput(raw) || '0';

    draftRef.current = next;
    setDraft(next);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(commit, COMMIT_DELAY_MS);
  }

  return (
    <TextField
      size='small'
      sx={{ width: '70px', textAlign: 'end' }}
      inputProps={{ style: { fontSize: 12 } }}
      InputProps={{
        endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 12 } }}>%</InputAdornment>,
      }}
      autoComplete="off"
      {...props}
      value={draft}
      onChange={handleChange}
      onBlur={commit}
      onFocus={(e) => e.target.select()}
    />
  );
}
